import { v4 as uuidv4 } from 'uuid';

interface CookieData {
  playerToken: string;
  playerName: string;
}

const COOKIE_NAME = 'queens_player';
const COOKIE_DAYS = 365;

function hasCookie(): boolean {
  return document.cookie.split('; ').some(c => c.startsWith(COOKIE_NAME + '='));
}

function readCookie(): CookieData {
  const match = document.cookie.split('; ').find(c => c.startsWith(COOKIE_NAME + '='));
  if (match) {
    try {
      const parsed = JSON.parse(decodeURIComponent(match.slice(COOKIE_NAME.length + 1)));
      return { playerToken: parsed.playerToken ?? '', playerName: parsed.playerName ?? '' };
    } catch {
      // fall through to default
    }
  }
  return { playerToken: uuidv4(), playerName: '' };
}

function writeCookie(data: CookieData): void {
  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_DAYS);
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(data))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

/**
 * This browser's player token, minting and **persisting** one the first time it is asked for.
 *
 * The write matters: `readCookie` hands back a fresh uuid when there is no cookie, so without
 * storing it every call returned a different token — a player with no cookie yet (one who has
 * never saved a name) was a new player to every request they made, and nothing filed under one of
 * those tokens could ever be read back.
 *
 * Safe against the migration bounce, which runs at module load before anything asks for a token:
 * by the time this can write a cookie, `migrateViaIframe` has already decided whether to bounce.
 */
export function getPlayerToken(): string {
  const data = readCookie();
  if (!data.playerToken) data.playerToken = uuidv4();
  if (!hasCookie()) writeCookie(data);
  return data.playerToken;
}

export function getPlayerName(): string {
  return readCookie().playerName;
}

export function switchSession(playerToken: string, playerName: string): void {
  writeCookie({ playerToken, playerName });
}

export function savePlayerName(name: string): void {
  const data = readCookie();
  data.playerName = name;
  writeCookie(data);
}

// Migrates player token from q.gibybo.com using a server-side redirect bounce.
// nginx on q.gibybo.com reads the cookie (first-party, bypasses storage partitioning)
// and redirects back to gibyqueens.com with the token in ?_qmt=.
export function migrateViaIframe(): Promise<void> {
  // Check if returning from the migration bounce (q.gibybo.com/_migrate → gibyqueens.com?_qmt=...)
  const params = new URLSearchParams(window.location.search);
  const qmt = params.get('_qmt');
  if (qmt !== null) {
    if (qmt) {
      try {
        const data = JSON.parse(qmt);
        if (data.playerToken) writeCookie(data);
      } catch {
        try {
          const data = JSON.parse(decodeURIComponent(qmt));
          if (data.playerToken) writeCookie(data);
        } catch {}
      }
    }
    // Import skips that were in q.gibybo.com localStorage
    const qms = params.get('_qms');
    if (qms) {
      const ids = qms.split(',').map(Number).filter(n => !isNaN(n) && n > 0);
      for (const id of ids) localStorage.setItem(`skipped-${id}`, '1');
      // _qb_skips_migrated is NOT set here so the upload-to-server step still runs
    }
    localStorage.setItem('_qb_migrated', '1');
    params.delete('_qmt');
    params.delete('_qms');
    const qs = params.size > 0 ? '?' + params : '';
    history.replaceState(null, '', window.location.pathname + qs + window.location.hash);
    return Promise.resolve();
  }

  if (hasCookie() || localStorage.getItem('_qb_migrated')) return Promise.resolve();

  // Bounce to q.gibybo.com/_migrate — reads cookie + localStorage skips first-party,
  // then redirects back to the current URL with _qmt and _qms params.
  window.location.replace('https://q.gibybo.com/_migrate?return=' + encodeURIComponent(window.location.href));
  return new Promise(() => {}); // page is navigating away, never resolves
}
